import React, { useEffect, useMemo, useRef, useState } from 'react';

// ─── helpers ────────────────────────────────────────────────────────────────

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

function hashString(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function maskIdFrom(seed) {
  return `m-${hashString(seed).toString(16)}`;
}

function gearToothPointsTrapezoid({ z, rTip, rRoot, tipFrac = 0.75, rootFrac = 0.25, phaseDeg = 0 }) {
  const pitch = (Math.PI * 2) / z;
  const phase = (phaseDeg * Math.PI) / 180;
  const pts = [];
  for (let i = 0; i < z; i++) {
    const a0 = i * pitch + phase;
    const a1 = (i + 1) * pitch + phase;
    const tipHalf = (pitch * tipFrac) / 2;
    const rootHalf = (pitch * rootFrac) / 2;
    const ar0 = a0 + rootHalf;
    const ar1 = a1 - rootHalf;
    const at0 = a0 + tipHalf;
    const at1 = a1 - tipHalf;
    pts.push({ x: rRoot * Math.cos(ar0), y: rRoot * Math.sin(ar0) });
    pts.push({ x: rTip * Math.cos(at0), y: rTip * Math.sin(at0) });
    pts.push({ x: rTip * Math.cos(at1), y: rTip * Math.sin(at1) });
    pts.push({ x: rRoot * Math.cos(ar1), y: rRoot * Math.sin(ar1) });
  }
  return pts;
}

function roundedPathFromPoints(pts, r) {
  const n = pts.length;
  const d = [];
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const l1 = dist(p1, p0);
    const l2 = dist(p1, p2);
    const rr = Math.min(r, l1 * 0.45, l2 * 0.45);
    const a = lerp(p1, p0, rr / l1);
    const b = lerp(p1, p2, rr / l2);
    if (i === 0) d.push(`M ${b.x.toFixed(3)} ${b.y.toFixed(3)}`);
    d.push(`L ${a.x.toFixed(3)} ${a.y.toFixed(3)}`);
    d.push(`Q ${p1.x.toFixed(3)} ${p1.y.toFixed(3)} ${b.x.toFixed(3)} ${b.y.toFixed(3)}`);
  }
  return d.join(' ') + ' Z';
}

function computeRatios(nodes, edges, rootId) {
  const map = new Map(nodes.map((n) => [n.id, n]));
  const adj = new Map();
  for (const n of nodes) adj.set(n.id, []);
  for (const [a, b] of edges) {
    adj.get(a).push(b);
    adj.get(b).push(a);
  }
  const ratio = new Map();
  ratio.set(rootId, 1);
  const q = [rootId];
  while (q.length) {
    const u = q.shift();
    const ru = ratio.get(u);
    const zu = map.get(u)?.z || 1;
    for (const v of adj.get(u) || []) {
      if (ratio.has(v)) continue;
      const zv = map.get(v)?.z || 1;
      ratio.set(v, -ru * (zu / zv));
      q.push(v);
    }
  }
  const out = {};
  for (const n of nodes) out[n.id] = ratio.get(n.id) ?? 0;
  return out;
}

// ─── scroll angle (scroll-only, smooth) ──────────────────────────────────────

function useScrollAngle() {
  const angleRef = useRef(0);
  const smoothRef = useRef(0);
  const rafRef = useRef(0);
  const [, setTick] = useState(0);

  useEffect(() => {
    const k = 0.18;
    const onScroll = () => {
      angleRef.current = window.scrollY * k;
      if (!rafRef.current) {
        rafRef.current = window.requestAnimationFrame(() => {
          rafRef.current = 0;
          const target = angleRef.current;
          smoothRef.current = smoothRef.current + (target - smoothRef.current) * 0.18;
          setTick((t) => (t + 1) % 1000000);
        });
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return smoothRef.current;
}

// ─── layout ──────────────────────────────────────────────────────────────────

function buildLayout(m) {
  const railW = 280;
  const addendum = 4;
  const dedendum = 4;
  const rPitch = (z) => (m * z) / 2;
  const zList = [10, 14, 20, 12, 18, 16, 22, 12, 20, 14];
  const laneA = 120;
  const laneB = 190;
  const nodes = [];
  let y = 160;

  for (let i = 0; i < zList.length; i++) {
    const z = zList[i];
    const x = i % 2 === 0 ? laneA : laneB;
    if (i > 0) {
      const prev = nodes[i - 1];
      const dx = Math.abs(x - prev.x);
      const C = rPitch(prev.z) + rPitch(z);
      const dy = dx < C ? Math.sqrt(Math.max(0, C * C - dx * dx)) : 2;
      y = prev.y + dy;
    }
    nodes.push({ id: `g${i}`, z, x, y, solid: z <= 12, variant: i % 2 === 0 ? 1 : 2 });
  }

  const addBranch = (attachId, z, dir, id) => {
    const a = nodes.find((n) => n.id === attachId);
    if (!a) return;
    const C = rPitch(a.z) + rPitch(z);
    const x = dir === 'out' ? a.x - C : a.x + C;
    nodes.push({ id, z, x, y: a.y, solid: z <= 12, variant: 2 });
  };

  addBranch('g2', 14, 'out', 'b0');
  addBranch('g5', 16, 'out', 'b1');

  const edges = [];
  for (let i = 0; i < zList.length - 1; i++) edges.push([`g${i}`, `g${i + 1}`]);
  edges.push(['g2', 'b0']);
  edges.push(['g5', 'b1']);

  const ratios = computeRatios(nodes, edges, 'g0');

  const pitchDeg = (z) => 360 / z;
  const norm = (a) => { let x = a % 360; if (x < 0) x += 360; return x; };

  const closestToothCenter = (phiDeg, phaseDeg, z) => {
    const p = pitchDeg(z);
    const local = norm(phiDeg - phaseDeg);
    const k = Math.round(local / p - 0.5);
    return norm(phaseDeg + (k + 0.5) * p);
  };
  const closestGapPhase = (gapAngleDeg, z) => {
    const p = pitchDeg(z);
    const k = Math.round(norm(gapAngleDeg) / p);
    return norm(gapAngleDeg - k * p);
  };

  const phases = {};
  const trunk = zList.map((_, i) => `g${i}`);
  phases[trunk[0]] = 0;

  for (let i = 0; i < trunk.length - 1; i++) {
    const u = trunk[i];
    const v = trunk[i + 1];
    const nu = nodes.find((n) => n.id === u);
    const nv = nodes.find((n) => n.id === v);
    const phi = (Math.atan2(nv.y - nu.y, nv.x - nu.x) * 180) / Math.PI;
    const toothAt = closestToothCenter(phi, phases[u], nu.z);
    const wantGapAt = norm(toothAt + 180);
    phases[v] = closestGapPhase(wantGapAt, nv.z);
  }

  for (const [a, b] of edges) {
    const base = trunk.includes(a) ? a : trunk.includes(b) ? b : null;
    const leaf = base === a ? b : base === b ? a : null;
    if (!base || !leaf) continue;
    const nb = nodes.find((n) => n.id === base);
    const nl = nodes.find((n) => n.id === leaf);
    const phi = (Math.atan2(nl.y - nb.y, nl.x - nb.x) * 180) / Math.PI;
    const toothAt = closestToothCenter(phi, phases[base], nb.z);
    const wantGapAt = norm(toothAt + 180);
    phases[leaf] = closestGapPhase(wantGapAt, nl.z);
  }

  return { railW, nodes, ratios, phases, addendum, dedendum, zList };
}

// ─── GearMaskContent ─────────────────────────────────────────────────────────

function GearMaskContent({ solid, variant, r, cavityR, spokeCount, spokeW, spokeLen, boreR, ringCount, holeR, holeCr }) {
  const winTop = clamp(r * 0.16, 16, 36);
  const winBot = clamp(r * 0.30, 22, 60);
  const winH = clamp(r * 0.28, 18, 36);
  const rr = clamp(r * 0.07, 6.0, 14.0);

  const dTrap = useMemo(() => {
    const pts = [
      { x: -winTop / 2, y: -winH / 2 },
      { x: winTop / 2, y: -winH / 2 },
      { x: winBot / 2, y: winH / 2 },
      { x: -winBot / 2, y: winH / 2 },
    ];
    return roundedPathFromPoints(pts, rr);
  }, [winTop, winBot, winH, rr]);

  if (solid) return null;

  if (variant === 2) {
    return (
      <>
        {Array.from({ length: ringCount }).map((_, i) => {
          const a = (i / ringCount) * Math.PI * 2;
          return <circle key={i} cx={Math.cos(a) * holeR} cy={Math.sin(a) * holeR} r={holeCr} fill="black" />;
        })}
      </>
    );
  }

  // variant 1: spokes + trapezoid windows
  return (
    <>
      <circle r={cavityR} fill="black" />
      {Array.from({ length: spokeCount }).map((_, i) => {
        const ang = (i / spokeCount) * 360;
        return (
          <g key={`sp${i}`} transform={`rotate(${ang})`}>
            <rect x={-spokeW / 2} y={-boreR * 1.2 - spokeLen} width={spokeW} height={spokeLen} rx={0} fill="white" />
          </g>
        );
      })}
      {Array.from({ length: spokeCount }).map((_, i) => {
        const ang = (i / spokeCount) * 360;
        const t = -(cavityR * 0.78);
        return (
          <g key={`w${i}`} transform={`rotate(${ang}) translate(0 ${t})`}>
            <path d={dTrap} fill="black" />
          </g>
        );
      })}
    </>
  );
}

// ─── Gear ────────────────────────────────────────────────────────────────────

function Gear({ id, m, z, x, y, baseAngleDeg, ratio, solid, variant, phaseDeg }) {
  const r = (m * z) / 2;
  const rTip = r + 4;
  const rRoot = Math.max(6, r - 4);

  const outline = useMemo(() => {
    const pts = gearToothPointsTrapezoid({ z, rTip, rRoot, tipFrac: 0.75, rootFrac: 0.25, phaseDeg });
    return roundedPathFromPoints(pts, 0.6);
  }, [z, rTip, rRoot, phaseDeg]);

  const boreR = Math.max(4, 0.8 * m);
  const bossR = clamp(boreR * 2.1, 10, r * 0.42);
  const cavityR = r * 0.72;
  const spokeCount = z <= 12 ? 0 : (z % 2 === 0 ? 6 : 8);
  const spokeLen = cavityR - boreR * 1.2;
  const spokeW = clamp(r * 0.14, 7, 18);
  const ringCount = 10 + (hashString(id) % 5);
  const holeR = r * 0.64;
  const holeCr = clamp(r * 0.075, 3.0, 10.0);
  const maskId = useMemo(() => maskIdFrom(`gear-${id}`), [id]);

  const s = (rTip + 18) * 2;
  const rot = baseAngleDeg * ratio;
  const fill = solid ? 'rgba(210,160,65,0.80)' : 'rgba(205,150,55,0.78)';
  const stroke = 'rgba(255,235,190,0.14)';
  const dark = 'rgba(0,0,0,0.36)';

  return (
    <div style={{ position: 'absolute', left: x - s / 2, top: y - s / 2, width: s, height: s, pointerEvents: 'none' }}>
      <svg width={s} height={s} viewBox={`${-s / 2} ${-s / 2} ${s} ${s}`} style={{ display: 'block' }}>
        <defs>
          <mask id={maskId}>
            <rect x={-s / 2} y={-s / 2} width={s} height={s} fill="black" />
            <path d={outline} fill="white" />
            <circle r={boreR} fill="black" />
            <GearMaskContent
              solid={solid}
              variant={solid ? 0 : variant}
              r={r}
              cavityR={cavityR}
              spokeCount={Math.max(6, spokeCount)}
              spokeW={spokeW}
              spokeLen={spokeLen}
              boreR={boreR}
              ringCount={ringCount}
              holeR={holeR}
              holeCr={holeCr}
            />
          </mask>
        </defs>
        <g transform={`rotate(${rot})`}>
          <path d={outline} fill={fill} stroke={stroke} strokeWidth={2} mask={`url(#${maskId})`} />
          <circle r={r * 0.92} fill="none" stroke={dark} strokeWidth={2} opacity={0.20} />
          <circle r={r * 0.86} fill="none" stroke={stroke} strokeWidth={2} opacity={0.18} />
          <circle r={bossR} fill={'rgba(0,0,0,0.14)'} stroke={stroke} strokeWidth={2} opacity={0.70} />
          <circle r={boreR * 1.25} fill={'rgba(0,0,0,0.22)'} stroke={dark} strokeWidth={2} opacity={0.80} />
        </g>
      </svg>
    </div>
  );
}

// ─── Rail ────────────────────────────────────────────────────────────────────

function Rail({ side, layout, m, baseAngle }) {
  const isLeft = side === 'left';
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: isLeft ? 0 : undefined,
        right: isLeft ? undefined : 0,
        width: layout.railW,
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'hidden',
        background: isLeft
          ? 'linear-gradient(90deg, rgba(0,0,0,0.45), rgba(0,0,0,0.00))'
          : 'linear-gradient(270deg, rgba(0,0,0,0.45), rgba(0,0,0,0.00))',
      }}
    >
      {layout.nodes.map((n) => {
        const ratio = layout.ratios[n.id] ?? 0;
        const phase = layout.phases[n.id] ?? 0;
        const x = isLeft ? n.x : layout.railW - n.x;
        return (
          <Gear
            key={`${side}-${n.id}`}
            id={`${side}-${n.id}`}
            m={m}
            z={n.z}
            x={x}
            y={n.y}
            baseAngleDeg={baseAngle}
            ratio={ratio}
            solid={n.solid}
            variant={n.variant}
            phaseDeg={phase}
          />
        );
      })}
    </div>
  );
}

// ─── GearBackground ──────────────────────────────────────────────────────────

export default function GearBackground({ children }) {
  const m = 10;
  const left = useMemo(() => buildLayout(m), []);
  const right = useMemo(() => buildLayout(m), []);
  const baseAngle = useScrollAngle();

  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at 50% 0%, rgba(70,90,130,0.18), rgba(0,0,0,0.92) 60%), linear-gradient(180deg, rgba(0,0,0,0.86), rgba(0,0,0,0.98))',
        color: 'rgba(255,255,255,0.92)',
        position: 'relative',
      }}
    >
      <Rail side="left" layout={left} m={m} baseAngle={baseAngle} />
      <Rail side="right" layout={right} m={m} baseAngle={baseAngle} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
}
